import { useContext, useEffect, useState } from "react";

import { faArrowLeft } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { styled } from "styled-components";
import invariant from "ts-invariant";

import { useDeleteUserCourseTemplate } from "api/hooks/useDeleteUserCourseTemplate";
import { useGetUserCourseQuery } from "api/hooks/useGetUserCourse";
import { GET_USER_COURSE_TEMPLATES_QUERY_KEY, useGetUserCourseTemplates } from "api/hooks/useGetUserCourseTemplates";
import type { FullUserCourse } from "api/schemas/UserCourse";
import { useProfileContext } from "contexts/ProfileContext";
import { Title } from "routes/library/components/styles";
import { Notification } from "shared/atoms/Notification";
import { usePageContentOffsetTop } from "shared/styles/usePageContentOffsetTop";
import { CurrentPatientContext } from "utils/contexts";

import { PatientDetails } from "./components/PatientDetails";
import { Templates } from "./components/Templates";

export type UserCourseView = "exercises" | "lessons";

export const NewUserCourseScreen = () => {
  const { profile } = useProfileContext();
  invariant(profile);
  const { patient } = useContext(CurrentPatientContext);
  const patientId = patient.id;
  const [courseData, setCourseData] = useState<FullUserCourse | undefined>(undefined);

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const offsetTop = usePageContentOffsetTop();

  const { error: courseIsError, refetch: refetchSavedCourseData } = useGetUserCourseQuery(
    { patientId },
    {
      enabled: false,
      onSuccess: data => {
        setCourseData(data);
      },
    }
  );

  const { data: templatesData, error: templatesIsError } = useGetUserCourseTemplates();

  const { mutate: deleteTemplateMutate, error: deleteTemplateIsError } = useDeleteUserCourseTemplate({
    onSuccess: () => {
      queryClient.invalidateQueries(GET_USER_COURSE_TEMPLATES_QUERY_KEY);
    },
  });

  const isError = courseIsError || templatesIsError || deleteTemplateIsError;

  useEffect(() => {
    if (patientId) {
      refetchSavedCourseData();
    }
  }, [patientId]);

  const goBack = () => navigate(-1);

  if (isError) {
    return (
      <div>
        <Notification variant="danger" style={{ marginBottom: "5px" }}>
          {t("errors.generic")}
        </Notification>
      </div>
    );
  }

  return (
    <Container>
      <TopContainer $offsetTop={offsetTop}>
        <TopContainerInner>
          <HeaderContainer>
            <>
              <div>
                <TopLink
                  onClick={() => {
                    goBack();
                  }}
                >
                  <FontAwesomeIcon icon={faArrowLeft} />
                  {t("buttons.back")}
                </TopLink>

                <Title>{t("usercourse.create_course")}</Title>
              </div>
              <PatientDetails ailment={patient.ailment} />
            </>
          </HeaderContainer>
        </TopContainerInner>
      </TopContainer>
      <BottomContainerOuter>
        <BottomContainerInner>
          <Templates
            hasUserCourseContent={!!(courseData?.exercises?.length || courseData?.lessons?.length)}
            templates={templatesData}
            onChangeTemplate={templateId => {
              navigate(`/patients/${patientId}/usercourse?initialTemplateId=${templateId}`);
            }}
            onDeleteTemplate={id => {
              deleteTemplateMutate({ therapistId: profile.id, templateId: id });
            }}
            onOpenExercisePicker={() => {
              navigate(`/patients/${patientId}/usercourse?showExercisePicker=true`);
            }}
            onContinueEditing={() => {
              navigate(`/patients/${patientId}/usercourse`);
            }}
          />
        </BottomContainerInner>
      </BottomContainerOuter>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-grow: 1;
  margin: 0 auto;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.greys.light2};
`;

const TopContainer = styled.div<{ $offsetTop: number }>`
  padding-top: ${({ $offsetTop }) => $offsetTop}px;
  background: ${({ theme }) => theme.colors.white};
`;

const TopContainerInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  max-width: 1200px;
  margin: 0 auto;
  background: ${({ theme }) => theme.colors.white};
  flex-grow: 1;
  ${props => props.theme.aboveBreakpoint} {
    padding: 40px;
  }
`;

const BottomContainerOuter = styled.div`
  background: ${({ theme }) => theme.colors.greys.light2};
`;

const BottomContainerInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;

  padding: 32px;
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 16px;
  ${props => props.theme.aboveBreakpoint} {
    flex-direction: row;
  }
`;

const TopLink = styled.div`
  color: ${props => props.theme.colors.redesign.b100};
  font-size: 16px;
  line-height: 24px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  margin-bottom: 4px;
  ${props => props.theme.belowBreakpoint} {
    justify-content: center;
  }
`;
